import { FC, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./VisualMapColorScale.module.less";
import { getUnit, isImperial } from "~/helpers/userPreference";
import { Typography } from "antd";

export interface VisualMapColor {
  min: number;
  max?: number;
  color: string;
}

interface VisualMapColorScaleProps {
  colorScale: VisualMapColor[];
  converter?: (value: number) => number;
  onChange?: (selectedIndexes: number[]) => void;
  filterKey?: string;
  disableClick?: boolean;
  showNone?: boolean;
  unit?: string;
}

export const VisualMapColorScale: FC<VisualMapColorScaleProps> = ({
  colorScale,
  onChange,
  filterKey,
  disableClick = true,
  converter,
  showNone = false,
  unit,
}) => {
  const [selectedScaleIndexes, setSelectedScaleIndexes] = useState<number[]>(
    [],
  );

  const isImperialUnit = useMemo(() => isImperial(), []);

  useEffect(() => {
    setSelectedScaleIndexes([]);
  }, [filterKey]);

  const onScaleChanged = useCallback(
    (selectedIndex?: number) => {
      let newIndexes: number[] = [];
      if (selectedIndex !== undefined) {
        newIndexes = selectedScaleIndexes.includes(selectedIndex)
          ? selectedScaleIndexes.filter((x) => x !== selectedIndex)
          : [...selectedScaleIndexes, selectedIndex];
      } else {
        newIndexes =
          selectedScaleIndexes.length > 0
            ? []
            : colorScale.map((_, index) => index);
      }

      onChange && onChange(newIndexes);
      setSelectedScaleIndexes(newIndexes);
    },
    [onChange, selectedScaleIndexes, colorScale],
  );

  const prepareScaleForRender = useCallback(
    (value: number) => {
      const convertedValue = converter ? converter(value) : value;

      return Number.isInteger(convertedValue)
        ? convertedValue
        : isImperialUnit
          ? convertedValue.toFixed(2)
          : convertedValue < 0
            ? Math.ceil(convertedValue)
            : convertedValue.toFixed(0);
    },
    [converter, isImperialUnit],
  );

  const getColorBrightnessAndThreshold = useCallback((color: string) => {
    const backgroundColor = color;

    const red = parseInt(backgroundColor.slice(1, 3), 16);
    const green = parseInt(backgroundColor.slice(3, 5), 16);
    const blue = parseInt(backgroundColor.slice(5, 7), 16);

    const brightness = 0.299 * red + 0.587 * green + 0.114 * blue;

    const brightnessThreshold = 128;

    return { brightness, brightnessThreshold };
  }, []);

  const unitByUserPref = useMemo(() => {
    return unit ? getUnit(unit) : "";
  }, [unit]);

  return (
    <div className={styles.visualMapContainer}>
      <table className={styles.scaleTable}>
        <thead>
          <tr className={styles.scaleRow}>
            {showNone && (
              <td
                className={styles.noneAllSelectionColumn}
                key={"none"}
                style={{
                  cursor: disableClick ? "auto" : "pointer",
                }}
                onClick={() => (disableClick ? undefined : onScaleChanged())}
              >
                <span className={styles.scaleValue}>
                  {selectedScaleIndexes.length === 0 ? "None" : "All"}
                </span>
              </td>
            )}
            {colorScale.map((x, i) => {
              const { brightness, brightnessThreshold } =
                getColorBrightnessAndThreshold(x.color);

              return (
                <td
                  className={styles.scaleColumn}
                  key={i}
                  style={{
                    backgroundColor: x.color,
                    border: selectedScaleIndexes.includes(i)
                      ? `1px solid ${x.color}`
                      : undefined,

                    cursor: disableClick ? "auto" : "pointer",
                  }}
                  onClick={() => (disableClick ? undefined : onScaleChanged(i))}
                >
                  <Typography.Title
                    level={2}
                    className={`${styles.scaleValue} ${
                      brightness < brightnessThreshold ? styles.whiteText : ""
                    }`}
                  >
                    {prepareScaleForRender(x.min)}
                    {x.max ? "" : " " + unitByUserPref}
                    {x.max
                      ? "-" +
                        prepareScaleForRender(x.max) +
                        " " +
                        unitByUserPref
                      : " < "}
                  </Typography.Title>
                </td>
              );
            })}
          </tr>
        </thead>
      </table>
    </div>
  );
};
